// src/pages/LandingPage.js

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import { useDoorloopjes } from '../context/DoorloopjesContext';

import Header from '../components/Header';
import Hero from '../components/Hero';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import RegistrationModal from '../components/RegistrationModal';
import HomepageContentItem from '../components/HomepageContentItem';
import InfoModal from '../components/InfoModal';
import SEO from '../components/SEO';
import { SITE_DESCRIPTION } from '../utils/seoText';
import { OVERZICHT_PAD, usePubliekeDoorloopjes } from '../utils/publiekeDoorloopjes';
import './PubliekeDoorloopjes.css';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

// Alleen paden binnen de site, zodat de redirect na het inloggen niet naar een ander domein kan.
function binnenSitePad(pad) {
  if (typeof pad !== 'string' || !pad.startsWith('/') || pad.startsWith('//') || pad === '/') {
    return null;
  }
  return pad;
}

function LandingPage() {
  const [pageData, setPageData] = useState(null);
  const [homepageItems, setHomepageItems] = useState({ beforeExample: [], afterExample: [] });
  const [loading, setLoading] = useState(true);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Pagina die iemand wilde openen zonder ingelogd te zijn (zie ProtectedRoute).
  const gevraagdePagina = binnenSitePad(location.state?.from);

  const { loadInitialData } = useDoorloopjes();
  const { doorloopjes: publiekeDoorloopjes } = usePubliekeDoorloopjes();

  const [redirectAfterLogin, setRedirectAfterLogin] = useState('/dashboard');
  const [loginMessage, setLoginMessage] = useState('');

  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [privacyData, setPrivacyData] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    const jwt = localStorage.getItem('jwt');
    if (jwt) {
      navigate(gevraagdePagina || '/dashboard', { replace: Boolean(gevraagdePagina) });
    } else if (gevraagdePagina) {
      // Direct het inlogvenster tonen en daarna naar de gevraagde pagina gaan.
      setRedirectAfterLogin(gevraagdePagina);
      setLoginMessage('Log in om verder te gaan.');
      setIsLoginModalOpen(true);
    }
  }, [navigate, gevraagdePagina]);

  useEffect(() => {
    const jwt = localStorage.getItem('jwt');
    if (!jwt) {
      const fetchPageData = async () => {
        setLoading(true);
        try {
          const [
            pageResponse,
            newsRes,
            articlesRes,
            digidatesRes,
            privacyRes,
            contactRes
          ] = await Promise.all([
            axios.get(
              `${STRAPI_URL}/api/landingpages?populate[page_sections][populate]=*&populate[uitleg_stappen][populate]=icon`
            ),
            axios.get(
              `${STRAPI_URL}/api/nieuws-items?filters[show_on_homepage][$eq]=true&populate[0]=featured_image&populate[1]=public_page`
            ),
            axios.get(
              `${STRAPI_URL}/api/artikels?filters[show_on_homepage][$eq]=true&populate[0]=featured_image&populate[1]=public_page`
            ),
            axios.get(
              `${STRAPI_URL}/api/digidates?filters[show_on_homepage][$eq]=true&populate[0]=image&populate[1]=public_page`
            ),
            axios.get(`${STRAPI_URL}/api/privacyverklaring?populate=*`),
            axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`)
          ]);

          if (pageResponse.data?.data?.length > 0) {
            setPageData(pageResponse.data.data[0]);
          }

          setPrivacyData(privacyRes.data.data);
          setContactData(contactRes.data.data);

          // Splits nieuws-items op basis van landing_page_position
          // Items met "none" of geen waarde worden behandeld als "boven"
          const newsItemBoven = newsRes.data.data.find(
            item => item.landing_page_position === 'boven' ||
                    item.landing_page_position === 'none' ||
                    !item.landing_page_position
          );
          const newsItemBeneden = newsRes.data.data.find(
            item => item.landing_page_position === 'beneden'
          );

          // Alle digidates die op homepage moeten komen (zowel aankomend als opnames)
          // Sorteer: aankomende eerst (op datum), daarna opnames (op datum aflopend)
          const allDigidates = digidatesRes.data.data.sort((a, b) => {
            // Als beide aankomend of beide opnames, sorteer op datum
            if (a.has_taken_place === b.has_taken_place) {
              return a.has_taken_place
                ? new Date(b.event_date) - new Date(a.event_date) // Opnames: nieuwste eerst
                : new Date(a.event_date) - new Date(b.event_date); // Aankomend: eerstvolgende eerst
            }
            // Aankomende voor opnames
            return a.has_taken_place ? 1 : -1;
          });

          // Alle artikelen
          const articles = articlesRes.data.data;

          // Bouw de volgorde voor de landingspagina
          // 1. Nieuwsitem "boven"
          // 2. Alle DigiDates (aankomend + opnames)
          // 3. [Ruimte voor AI assistent - later]
          // 4. [Voorbeeld "Wat is een Doorloopje" - statisch in JSX]
          // 5. Nieuwsitem "beneden"
          // 6. Overige artikelen

          // Splits items in twee groepen: voor en na het voorbeeld
          const itemsBeforeExample = [];
          const itemsAfterExample = [];

          if (newsItemBoven) {
            itemsBeforeExample.push({...newsItemBoven, __type: 'nieuws-item'});
          }

          // Voeg alle digidates toe aan beforeExample
          allDigidates.forEach(digidate => {
            itemsBeforeExample.push({...digidate, __type: 'digidates'});
          });

          // Positie 3 is voor AI assistent (later)
          // Positie 4 is voor voorbeeld (statisch in JSX)

          if (newsItemBeneden) {
            itemsAfterExample.push({...newsItemBeneden, __type: 'nieuws-item'});
          }

          // Voeg alle artikelen toe
          articles.forEach(article => {
            itemsAfterExample.push({...article, __type: 'artikel'});
          });

          setHomepageItems({ beforeExample: itemsBeforeExample, afterExample: itemsAfterExample });
        } catch (error) {
          console.error(
            'Fout bij het ophalen van de landingspagina data:',
            error
          );
        } finally {
          setLoading(false);
        }
      };
      fetchPageData();
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    const exampleSections = document.querySelectorAll('.example-doorloopje-section');
    exampleSections.forEach(section => {
      observer.observe(section);
    });

    return () => {
      exampleSections.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, [homepageItems]);

  const handleLoginSuccess = async () => {
    setIsLoginModalOpen(false);
    await loadInitialData();
    // Kwam iemand via een link naar een beschermde pagina, vervang dan de landingspagina in
    // de geschiedenis. Anders stuurt "terug" direct weer door naar dezelfde pagina.
    navigate(redirectAfterLogin, { replace: redirectAfterLogin === gevraagdePagina });
  };

  const handleRegisterSuccess = async () => {
    setIsRegisterModalOpen(false);
    await loadInitialData();
    navigate('/dashboard');
  };

  const handleLoginClick = (redirect = '/dashboard', message = '') => {
    setRedirectAfterLogin(redirect);
    setLoginMessage(message);
    setIsLoginModalOpen(true);
  };

  const handleRegisterClick = () => {
    setIsRegisterModalOpen(true);
  };

  if (loading && !privacyData) return <p>Pagina wordt geladen...</p>;

  const heroSectionData = pageData?.page_sections.find(
    (s) => s.__component === 'page-sections.hero-section'
  );

  const CheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
      <g fill="none" stroke="#2F3136" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="25" cy="25" r="23" />
        <polyline points="17 26 22 31 33 18" />
      </g>
    </svg>
  );

  const FeatureList = () => (
    <div className="feature-list">
      <span><CheckIcon /> Gebaseerd op onderzoek</span>
      <span><CheckIcon /> Vind een antwoord op elke uitdaging met de assistent</span>
      <span><CheckIcon /> Direct toepasbaar in de klas</span>
    </div>
  );

  return (
    <div className="page-container landing-page">
      <SEO
        title="Versterk je lespraktijk | Doorloopjes"
        url="/"
        type="website"
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Doorloopjes',
            url: 'https://doorloopjes.nl',
            logo: 'https://doorloopjes.nl/images/logo.png',
            description: SITE_DESCRIPTION,
            sameAs: [],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Doorloopjes',
            url: 'https://doorloopjes.nl',
            description: SITE_DESCRIPTION,
            inLanguage: 'nl',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'EducationalOrganization',
            name: 'Doorloopjes',
            url: 'https://doorloopjes.nl',
            description: SITE_DESCRIPTION,
          }
        ]}
      />
      <Header
        onLoginClick={handleLoginClick}
        onRegisterClick={handleRegisterClick}
      />

      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={handleLoginSuccess}
          message={loginMessage}
          onOpenRegister={() => {
            setIsLoginModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}

      {isRegisterModalOpen && (
        <RegistrationModal
          onClose={() => setIsRegisterModalOpen(false)}
          onRegisterSuccess={handleRegisterSuccess}
        />
      )}

      <InfoModal
        show={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        title={privacyData?.title}
        content={privacyData?.body}
        loading={loading}
        showSocial={false}
      />

      <InfoModal
        show={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title={contactData?.title}
        content={contactData?.body}
        loading={loading}
        showSocial={true}
      />

      <div className="page-introduction">
        {heroSectionData && <Hero data={heroSectionData} onLoginClick={handleLoginClick} />}
        <FeatureList />
      </div>

      <main className="landing-page-main">
        {/* Uitleg-stappen */}
        {pageData?.uitleg_stappen?.length > 0 && (
          <section className="how-to-section">
            <h2>Waar wil jij aan werken?</h2>
            <div className="how-to-columns">
              {pageData.uitleg_stappen.map((stap, index) => (
                <div
                  className={`how-to-column ${stap.slug ? 'how-to-column-clickable' : ''}`}
                  key={index}
                  onClick={stap.slug ? () => navigate(`/onderwerp/${stap.slug}`) : undefined}
                  style={stap.slug ? { cursor: 'pointer' } : undefined}
                  role={stap.slug ? 'link' : undefined}
                >
                  <div className="how-to-column-header">
                    <div className="how-to-icon-placeholder">
                      {stap.icon?.url && (
                        <img
                          src={stap.icon.url}
                          alt={stap.title || ''}
                        />
                      )}
                    </div>
                    <h3>{stap.title}</h3>
                  </div>
                  <p>{stap.description}</p>
                  {stap.slug && <span className="how-to-more">Bekijk de doorloopjes →</span>}
                </div>
              ))}
            </div>
            {publiekeDoorloopjes && (
              <Link to={OVERZICHT_PAD} className="alle-doorloopjes-link">
                Alles voor je lespraktijk →
              </Link>
            )}
          </section>
        )}

        {/* Items voor het voorbeeld (posities 1-2) */}
        {homepageItems.beforeExample.length > 0 && (
          <div className="homepage-dynamic-content">
            {homepageItems.beforeExample.map((item, index) => {
              // Bepaal de achtergrondkleur: geel voor digidates, lichtgrijs voor even indices
              let bgClass = '';
              if (item.__type === 'digidates') {
                bgClass = 'bg-yellow';
              } else if (index % 2 === 0) {
                bgClass = 'bg-light-grey';
              }

              return (
                <div
                  key={`${item.__type}-${item.id}`}
                  className={`dynamic-content-wrapper ${bgClass}`}
                >
                  <HomepageContentItem
                    item={item}
                    type={item.__type}
                    alignment={index % 2 === 0 ? 'left' : 'right'}
                    onLoginClick={handleLoginClick}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* AI-assistent sectie (positie 3) */}
        <section className={`example-doorloopje-section ${
          homepageItems.beforeExample.length % 2 === 0 ? 'bg-light-grey' : ''
        }`}>
          <div className={`example-content ${
            homepageItems.beforeExample.length % 2 === 0 ? '' : 'reverse'
          }`}>
            <div
              className="example-image ai-assistant-image"
              onClick={() => handleLoginClick('/dashboard', 'Log in om de assistent te gebruiken.')}
              style={{ cursor: 'pointer' }}
            >
              <img
                src="https://storage.googleapis.com/doorloopjes_website/small_voorbeeld_ai_assistent_2_443e8c3dea/small_voorbeeld_ai_assistent_2_443e8c3dea.png"
                alt="AI Assistent"
              />
            </div>
            <div className="example-text">
              <h2>Maak kennis met onze assistent</h2>
              <p>Stel je vragen over didactiek en je lessen, en krijg direct antwoord. De assistent zoekt doorloopjes die bij je passen. Hij geeft advies op maat en helpt je je lessen voor te bereiden.</p>
            </div>
          </div>
        </section>

        {/* Statische Voorbeeld sectie (positie 4) */}
        <section className={`example-doorloopje-section ${
          (homepageItems.beforeExample.length + 1) % 2 === 0 ? 'bg-light-grey' : ''
        }`}>
          <div className={`example-content ${
            (homepageItems.beforeExample.length + 1) % 2 === 0 ? '' : 'reverse'
          }`}>
            <div
              className="example-image"
              onClick={() => navigate('/voorbeeld/example-doorloopje-de-les-beginnen')}
              style={{ cursor: 'pointer' }}
            >
              <img
                src="https://storage.googleapis.com/doorloopjes_website/de_les_beginnen_a5d650caec/de_les_beginnen_a5d650caec.png"
                alt="Voorbeeld Doorloopje"
              />
            </div>
            <div className="example-text">
              <p className="example-label">VOORBEELD</p>
              <h2>Wat is een doorloopje?</h2>
              <p>Bekijk hoe een doorloopje eruitziet en hoe je het in de klas gebruikt.</p>
              <a href="/voorbeeld/example-doorloopje-de-les-beginnen" className="btn-primary">
                Bekijk voorbeeld
              </a>
            </div>
          </div>
        </section>

        {/* Items na het voorbeeld (posities 5+) */}
        {homepageItems.afterExample.length > 0 && (
          <div className="homepage-dynamic-content">
            {homepageItems.afterExample.map((item, index) => {
              // Bereken de totale index rekening houdend met items voor het voorbeeld
              // +2 voor de AI-assistent (positie 3) en voorbeeld (positie 4)
              const totalIndex = homepageItems.beforeExample.length + 2 + index;

              // Bepaal de achtergrondkleur: geel voor digidates, lichtgrijs voor even indices
              let bgClass = '';
              if (item.__type === 'digidates') {
                bgClass = 'bg-yellow';
              } else if (totalIndex % 2 === 0) {
                bgClass = 'bg-light-grey';
              }

              return (
                <div
                  key={`${item.__type}-${item.id}`}
                  className={`dynamic-content-wrapper ${bgClass}`}
                >
                  <HomepageContentItem
                    item={item}
                    type={item.__type}
                    alignment={totalIndex % 2 === 0 ? 'left' : 'right'}
                    onLoginClick={handleLoginClick}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <section className="cta-section-yellow">
          <div className="cta-content">
            <h2>Maak nu een account aan en krijg gratis toegang tot de app</h2>
          </div>
          <div className="cta-action">
            <button onClick={handleRegisterClick} className="btn-primary">
              Registreren
            </button>
          </div>
        </section>
      </main>

      <Footer
        onLoginClick={handleLoginClick}
        onPrivacyClick={() => setIsPrivacyModalOpen(true)}
        onContactClick={() => setIsContactModalOpen(true)}
      />
    </div>
  );
}

export default LandingPage;
